import type { ApiRoomJSON } from 'mastodon/api_types/rooms';

/**
 * The host's presence heartbeat (ticket 69).
 *
 * A room connection proves a tab is open, not that the host is taking part —
 * and since the room started holding a screen wake lock (8.85) a forgotten tab
 * stays connected indefinitely. So the host's own client reports presence.
 *
 * What counts as present is the reporter's definition, not ours: AFK here means
 * "away: inactive voice or stream". So the ONLY evidence accepted is
 *
 *   - voice: the host was actually heard speaking in the room, or
 *   - stream: the host is publishing camera or screen share from this tab.
 *
 * (The third case, a host broadcasting through OBS with no browser tab at all,
 * cannot be reported from here and is handled server-side: the reaper marks the
 * host active while their ingest is publishing.)
 *
 * Deliberately NOT input, and deliberately not a bare timer. Touching the
 * screen, scrolling the chat or simply having the tab in front is not taking
 * part in a voice room, and a timer would report a tab nobody has looked at
 * since yesterday as a host hard at work. A host who sits silently for the
 * whole AFK window IS away by this definition, which is the point.
 */

/** How often we consider sending. Sets how fast a returning host clears the flag. */
export const HOST_ACTIVITY_TICK_MS = 15_000;

/** Never more than one beat per this window, whatever the user does. */
export const HOST_ACTIVITY_MIN_GAP_MS = 60_000;

export interface HostActivityBeaconOptions {
  /** Posts the heartbeat. Rejections are the caller's to swallow. */
  send: () => void;
  /** Injectable for tests. */
  now?: () => number;
  /**
   * Is the host publishing video (camera or screen share) right now? Level
   * triggered, so a silent stream keeps reporting for as long as it runs.
   */
  isStreaming?: () => boolean;
  minGapMs?: number;
}

export interface HostActivityBeacon {
  /**
   * The host was heard speaking. Edge triggered and cheap: sets a flag, sends
   * nothing. Speech comes in bursts far shorter than the send gap, so it is
   * remembered until the next tick rather than sampled and missed.
   */
  noteVoice: () => void;
  /** Called on an interval. Sends only if there is something to report. */
  tick: () => void;
}

export function createHostActivityBeacon({
  send,
  now = () => Date.now(),
  isStreaming = () => false,
  minGapMs = HOST_ACTIVITY_MIN_GAP_MS,
}: HostActivityBeaconOptions): HostActivityBeacon {
  let heardVoice = false;
  // -Infinity, not 0: the first genuine evidence beats immediately rather
  // than waiting out a gap it never used.
  let lastSentAt = -Infinity;

  return {
    noteVoice() {
      heardVoice = true;
    },

    tick() {
      if (!heardVoice && !isStreaming()) return;
      if (now() - lastSentAt < minGapMs) return;

      heardVoice = false;
      lastSentAt = now();
      send();
    },
  };
}

/**
 * May this viewer end the room?
 *
 * The host always may. A co-host may only while the host is AFK — the other
 * case the ticket names, "when the host leaves the room", needs nothing here:
 * the co-host who inherits the room IS the host by then, so it is the first
 * clause. Mirrors `RoomPolicy#destroy?`; the server is what actually enforces
 * it, this only decides whether to draw the button.
 */
export function canEndRoom({
  isHost,
  myRole,
  room,
}: {
  isHost: boolean;
  myRole: string | undefined;
  room: Pick<ApiRoomJSON, 'host_afk'>;
}): boolean {
  if (isHost) return true;

  // `host_afk` absent (an older server) reads as false, which is the
  // pre-8.87 behaviour: no co-host button at all.
  return myRole === 'cohost' && room.host_afk === true;
}

/** "over an hour", "over 3 hours" — how long the host has been gone, in words. */
export function describeAway(seconds: number | null | undefined): string {
  if (!seconds || seconds < 3600) return 'over an hour';

  const hours = Math.floor(seconds / 3600);
  return hours < 2 ? 'over an hour' : `over ${hours} hours`;
}

export interface EndRoomConfirmation {
  // The CONFIRM modal takes an open record; a closed interface is rejected
  // there even when every field it needs is present.
  [key: string]: unknown;
  title: string;
  message: string;
  confirm: string;
  onConfirm: () => void;
}

/**
 * What a co-host sees before ending somebody else's room.
 *
 * Confirmed, unlike the host's own End Room button, because the person
 * pressing it does not own the room: it disconnects everyone, stops the
 * host's recording, and the host cannot undo it when they come back.
 */
export function afkEndConfirmation(
  awaySeconds: number | null | undefined,
  onEnd: () => void,
): EndRoomConfirmation {
  return {
    title: 'End the room?',
    message: `The host has been away for ${describeAway(awaySeconds)}. Ending the room disconnects everyone still in it and stops any recording. This cannot be undone.`,
    confirm: 'End room',
    onConfirm: onEnd,
  };
}
